import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Popover, Tooltip, Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { QuestionCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  fetchHelpBootstrap,
  helpAssistantQueryKeys,
  postHelpEvent,
  type HelpBootstrap,
} from './api/helpAssistantApi';
import { buildHelpRouteContext, isPublicHelpRoute } from './routeMap';
import { HELP_LAUNCHER_Z_INDEX, HELP_PANEL_WIDTH } from './helpPanelLayout';

/**
 * The panel — and every byte of chat UI with it — is only downloaded once the
 * user actually opens the assistant.
 */
const HelpAssistantPanel = React.lazy(
  () =>
    import(
      /* webpackChunkName: "help-assistant-panel" */ './components/HelpAssistantPanel'
    ),
);

const SEEN_FEATURE_STORAGE_KEY = 'helpAssistant.seenNewFeature';
/** Permanently dismissed via "Nicht mehr anzeigen". */
const INTRO_DISMISSED_STORAGE_KEY = 'helpAssistant.introDismissed';
/**
 * Page keys that re-trigger the intro balloon.
 *
 * The balloon used to appear once per browser session, which meant daily users
 * effectively never saw it and never learned the assistant exists. It now shows
 * on every arrival at the dashboard — the one page everybody passes through —
 * while "Nicht mehr anzeigen" still switches it off for good.
 */
const INTRO_PAGE_KEYS = new Set(['dashboard']);
/** The balloon closes by itself — it must never sit in the way. */
const INTRO_AUTO_HIDE_MS = 14000;
const MOBILE_BREAKPOINT = 768;

const readStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode / storage disabled — the flag simply is not remembered */
  }
};

const Launcher = styled.div<{ $shift: number }>`
  position: fixed;
  /* Slides left of the open desktop panel so it never sits on top of the chat. */
  right: calc(max(16px, env(safe-area-inset-right)) + ${(props) => props.$shift}px);
  bottom: max(16px, env(safe-area-inset-bottom));
  z-index: ${HELP_LAUNCHER_Z_INDEX};
  transition: right 0.25s ease;

  .ant-btn {
    width: 48px;
    height: 48px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.22);
  }

  .ant-btn:focus-visible {
    outline: 3px solid #fff;
    outline-offset: 2px;
  }
`;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
};

/**
 * Global in-app product assistant.
 *
 * Mounted once in <AppShell>, i.e. ABOVE the router outlet: the open flag and
 * the whole conversation therefore survive navigation untouched, and the panel
 * stays open while the user walks through the app. Renders nothing on public
 * routes and while nobody is logged in.
 */
const HelpAssistantWidget: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const location = useLocation();

  // Deliberately not persisted across page loads: the assistant covers part of
  // the page, so every visit starts with it closed. Navigating inside the app
  // keeps it open (this state lives above the router outlet).
  const [open, setOpen] = useState(false);
  // Once mounted the panel stays mounted, so closing and reopening keeps the
  // conversation and costs no second download.
  const [everOpened, setEverOpened] = useState(false);
  const [seenFeatureId, setSeenFeatureId] = useState(
    () => readStorage(SEEN_FEATURE_STORAGE_KEY) ?? '',
  );
  // Short intro next to the button: what the assistant does and where bugs and
  // ideas go. Shown on every dashboard visit, dismissible for good — an
  // auto-opening panel would cover the page the user came for.
  const [introOpen, setIntroOpen] = useState(false);
  const isMobile = useIsMobile();

  const { pageKey, currentRoute } = useMemo(
    () => buildHelpRouteContext(location.pathname, location.search),
    [location.pathname, location.search],
  );
  const pageKeyRef = useRef(pageKey);
  pageKeyRef.current = pageKey;

  const hidden = !isAuthenticated || !user || isPublicHelpRoute(location.pathname);

  // No request on page load: the query is disabled until the panel has been
  // opened at least once, then react-query caches it for the session.
  const bootstrapQuery = useQuery<HelpBootstrap>({
    queryKey: helpAssistantQueryKeys.bootstrap(language),
    queryFn: () => fetchHelpBootstrap(language, pageKeyRef.current),
    enabled: everOpened && !hidden,
    retry: false,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const isIntroPage = INTRO_PAGE_KEYS.has(pageKey);
  // Read inside the effect instead of being a dependency, so closing the panel
  // on the dashboard does not immediately pop the balloon back up.
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    // Only once the widget is actually visible: on the login page it renders
    // nothing, and marking the flag there swallowed the balloon afterwards.
    if (hidden) return;
    // Re-runs on every arrival at the dashboard, because `isIntroPage` flips
    // back to true each time the user navigates there.
    if (!isIntroPage) return;
    if (openRef.current) return;
    if (readStorage(INTRO_DISMISSED_STORAGE_KEY) === 'true') return;
    const show = window.setTimeout(() => setIntroOpen(true), 1200);
    const hide = window.setTimeout(() => setIntroOpen(false), 1200 + INTRO_AUTO_HIDE_MS);
    return () => {
      window.clearTimeout(show);
      window.clearTimeout(hide);
    };
  }, [hidden, isIntroPage]);

  const dismissIntro = useCallback((permanently: boolean) => {
    setIntroOpen(false);
    if (permanently) writeStorage(INTRO_DISMISSED_STORAGE_KEY, 'true');
  }, []);

  const openPanel = useCallback(() => {
    setEverOpened(true);
    setOpen(true);
    setIntroOpen(false);
    postHelpEvent({ event: 'help_chat_opened', pageKey: pageKeyRef.current });
  }, []);

  const closePanel = useCallback(() => {
    setOpen(false);
  }, []);

  const latestFeatureId = bootstrapQuery.data?.newFeatures?.[0]?.id ?? '';
  const showNewBadge = Boolean(
    bootstrapQuery.data?.hasNew && latestFeatureId && latestFeatureId !== seenFeatureId,
  );

  const markNewFeaturesSeen = useCallback(() => {
    if (!latestFeatureId) return;
    setSeenFeatureId(latestFeatureId);
    writeStorage(SEEN_FEATURE_STORAGE_KEY, latestFeatureId);
  }, [latestFeatureId]);

  if (hidden) return null;

  return (
    <>
      <Launcher $shift={open && !isMobile ? HELP_PANEL_WIDTH : 0} hidden={open && isMobile}>
        <Badge dot={showNewBadge} offset={[-6, 6]} color="#eb2f96">
          <Popover
            open={introOpen && !open}
            placement="leftBottom"
            arrow
            overlayStyle={{ maxWidth: 320 }}
            content={
              <div style={{ maxWidth: 300 }}>
                <Typography.Paragraph style={{ marginBottom: 6, fontSize: 13 }}>
                  {t('helpAssistant.introChat')}
                </Typography.Paragraph>
                <Typography.Paragraph type="secondary" style={{ marginBottom: 10, fontSize: 13 }}>
                  {t('helpAssistant.introFeedback')}
                </Typography.Paragraph>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Button size="small" type="text" onClick={() => dismissIntro(true)}>
                    {t('helpAssistant.introNeverAgain')}
                  </Button>
                  <Button size="small" type="primary" onClick={openPanel}>
                    {t('helpAssistant.introOpen')}
                  </Button>
                </div>
              </div>
            }
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span>{t('helpAssistant.title')}</span>
                <Button
                  size="small"
                  type="text"
                  icon={<CloseOutlined />}
                  aria-label={t('helpAssistant.close')}
                  onClick={() => dismissIntro(false)}
                />
              </div>
            }
          >
          <Tooltip
            title={open ? t('helpAssistant.launcherClose') : t('helpAssistant.launcherOpen')}
            placement="left"
          >
            <Button
              type="primary"
              shape="circle"
              size="large"
              icon={<QuestionCircleOutlined style={{ fontSize: 22 }} />}
              onClick={open ? closePanel : openPanel}
              aria-label={
                open ? t('helpAssistant.launcherClose') : t('helpAssistant.launcherOpen')
              }
              aria-expanded={open}
            />
          </Tooltip>
          </Popover>
        </Badge>
      </Launcher>

      {everOpened && (
        <Suspense fallback={null}>
          <HelpAssistantPanel
            open={open}
            onClose={closePanel}
            isMobile={isMobile}
            bootstrap={bootstrapQuery.data}
            bootstrapFailed={bootstrapQuery.isError}
            pageKey={pageKey}
            currentRoute={currentRoute}
            onNewFeaturesSeen={markNewFeaturesSeen}
          />
        </Suspense>
      )}
    </>
  );
};

export default HelpAssistantWidget;
